import React, { useCallback, useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, RotateCcw, Sparkles, Music, AlertCircle, Eye, Home, Brain, Leaf, Aperture, X } from 'lucide-react';
import { Logo } from './Logo';
import { useClickSound } from '../hooks/useClickSound';
declare global {
  interface Window {
    FaceMesh: any;
    Camera: any;
    drawConnectors: any;
    FACEMESH_TESSELATION: any;
    FACEMESH_RIGHT_EYE: any;
    FACEMESH_LEFT_EYE: any;
    FACEMESH_LIPS: any;
    FACEMESH_FACE_OVAL: any;
  }
}
interface EmotionScannerProps {
  energyPoints: number;
  onDeductPoints: (amount: number) => void;
  onOpenEnergyPopup: () => void;
  onClose: () => void;
  onGoHome: () => void;
  onStartRitual?: (statusCategory: string) => void;
}
interface AIReport {
  status_kategorii: 'STRESS' | 'SAD' | 'NEUTRAL' | 'HAPPY';
  aura_color: string;
  wibracja_procent: number;
  body_signals: string;
  environment_resonance: string;
}
type ScannerStatus = 'loading' | 'ready' | 'scanning' | 'complete' | 'generating_report' | 'showing_report' | 'camera_error';
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}
export interface TherapyConfig {
  sound_hz: number;
  sound_name: string;
  breathing_technique: string;
  breathing_name: string;
  breathing_pattern: number[];
  ritual_title: string;
  ritual_description: string;
  icon: any;
}
export const THERAPY_MATRIX: Record<string, TherapyConfig> = {
  STRESS: {
    sound_hz: 432,
    sound_name: '432 Hz — Częstotliwość Ziemi',
    breathing_technique: 'box',
    breathing_name: 'Oddech Pudełkowy',
    breathing_pattern: [4, 4, 4, 4],
    ritual_title: 'Rytuał Uziemienia',
    ritual_description: 'Skonfigurujemy dźwięk na 432 Hz by zredukować stres, a następnie zresetujemy układ nerwowy oddechem pudełkowym.',
    icon: Music
  },
  SAD: {
    sound_hz: 528,
    sound_name: '528 Hz — Częstotliwość Odnowy',
    breathing_technique: '478',
    breathing_name: 'Oddech 4-7-8',
    breathing_pattern: [4, 7, 8],
    ritual_title: 'Rytuał Odnowy',
    ritual_description: 'Zastosujemy wibrację 528 Hz i głęboki oddech 4-7-8, by łagodnie usunąć blokady i zaprosić do ciała nową energię.',
    icon: Leaf
  },
  NEUTRAL: {
    sound_hz: 417,
    sound_name: '417 Hz — Rozpuszczanie Schematów',
    breathing_technique: 'cleansing',
    breathing_name: 'Oddech Oczyszczający',
    breathing_pattern: [4, 0, 6],
    ritual_title: 'Rytuał Balansujący',
    ritual_description: 'Twój stan jest idealny do pracy z podświadomością. Oczyścimy resztki napięć wibracją 417 Hz i przygotujemy umysł na nowe kody.',
    icon: Brain
  },
  HAPPY: {
    sound_hz: 852,
    sound_name: '852 Hz — Przebudzenie Intuicji',
    breathing_technique: 'resonance',
    breathing_name: 'Oddech Rezonansowy',
    breathing_pattern: [5, 0, 5],
    ritual_title: 'Rytuał Witalności',
    ritual_description: 'Masz w sobie piękną, wysoką wibrację. Wykorzystamy 852 Hz i oddech rezonansowy, by przekuć ją w potężną intuicję i kreatywność na resztę dnia.',
    icon: Sparkles
  }
};
const MOCK_REPORTS: AIReport[] = [{
  status_kategorii: 'STRESS',
  aura_color: 'Ametystowy',
  wibracja_procent: 55,
  body_signals: 'Wyczuwam, że Twoje pole energetyczne niesie w sobie ślady intensywnych transformacji. Pod warstwą zewnętrznego opanowania kryje się skumulowana energia, która szuka ujścia — Twój splot słoneczny domaga się uwolnienia.',
  environment_resonance: 'Przestrzeń wokół Ciebie rezonuje wysoką częstotliwością, która potęguje wewnętrzne napięcie. Potrzebuje elementu uziemienia, by wspierać Twój przepływ.'
}, {
  status_kategorii: 'SAD',
  aura_color: 'Srebrzysty',
  wibracja_procent: 42,
  body_signals: 'Wyczuwam, że Twoja energia przechodzi przez fazę cichej regeneracji. Twoje pole wibracyjne jest przyciszone, jakby ciało i dusza prosiły o moment absolutnej łagodności i troski wobec siebie.',
  environment_resonance: 'Otoczenie wokół Ciebie współgra z Twoim wewnętrznym stanem — jest wyciszone i intymne. To przestrzeń, która sprzyja głębokiemu wejrzeniu w siebie.'
}, {
  status_kategorii: 'NEUTRAL',
  aura_color: 'Szafirowy',
  wibracja_procent: 68,
  body_signals: 'Twoja energia jest stabilna i zrównoważona — jak spokojna tafla jeziora. Wyczuwam w Tobie gotowość do głębszej pracy, choć pod powierzchnią drzemią jeszcze subtelne napięcia, które czekają na rozpuszczenie.',
  environment_resonance: 'Przestrzeń wokół Ciebie jest otwarta i neutralna — jak czyste płótno gotowe na nowy obraz. To idealny moment, by świadomie nadać jej intencję.'
}, {
  status_kategorii: 'HAPPY',
  aura_color: 'Złoty',
  wibracja_procent: 88,
  body_signals: 'Twoja energia promieniuje ciepłem i otwartością — wyczuwam naturalną świetlistość, która otacza Twoje pole energetyczne. Twoja prezencja jest magnetyczna i uziemiona jednocześnie, co świadczy o głębokim połączeniu ze sobą.',
  environment_resonance: 'Przestrzeń wokół Ciebie wibruje jasną, ciepłą częstotliwością. To otoczenie, które wspiera i wzmacnia Twój wysoki stan wibracyjny.'
}, {
  status_kategorii: 'STRESS',
  aura_color: 'Rubinowy',
  wibracja_procent: 45,
  body_signals: 'Wyczuwam zablokowaną energię, która krąży w zamkniętej pętli — Twoje ciało energetyczne jest w trybie ochronnym, brakuje mu miękkości i odpuszczenia. Przepływ domaga się uwolnienia przez świadomy oddech.',
  environment_resonance: 'Energia w Twojej przestrzeni jest gęsta i nieruchoma, jakby czekała na impuls do transformacji. Rytuał oczyszczenia pomoże ją wprawić w ruch.'
}];
function compressImage(dataUrl: string, maxWidth = 1024, quality = 0.7): Promise<string> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height, 1);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.src = dataUrl;
  });
}
const SCANNER_SYSTEM_PROMPT = `Jesteś "Aura AI" – elitarnym, empatycznym systemem w luksusowej aplikacji do biohackingu i odnowy energetycznej. Otrzymasz zdjęcie użytkownika zrobione przed chwilą z przedniej kamery ORAZ aktualną godzinę. Twoim zadaniem jest ocenić jego stan energetyczny na podstawie twarzy (mikronapięcia, oczy), stanu prezencji fizycznej, otoczenia (tła) i pory dnia.

ZASADY TONU WYPOWIEDZI (CRITICAL):
1. ZABRONIONE jest używanie języka klinicznego, medycznego i technicznego (np. "mięsień jarzmowy", "brak zmarszczek", "widzę pokój", "osoba na zdjęciu", "włosy", "potargane", "zaspany").
2. Zwracaj się bezpośrednio do użytkownika w 2. osobie liczby pojedynczej ("Twoja energia", "Wyczuwam", "Widzę").
3. Używaj słów z żargonu biohackingu i mindfulness: wibracja, uziemienie, zablokowana energia, przepływ, przebodźcowanie, harmonia, rezonans, świetlistość, prezencja.
4. NIGDY nie opisuj dosłownie tego co widzisz na zdjęciu (np. "widzę zmęczone oczy", "potargane włosy"). Zamiast tego INTERPRETUJ energetycznie: "wyczuwam, że Twoje pole energetyczne niesie ślady intensywnego dnia".

KONTEKST PORY DNIA (CRITICAL):
- Otrzymasz aktualną godzinę użytkownika. MUSISZ ją uwzględnić w interpretacji.
- 22:00–04:00 = NOCNA PORA: Nie mów o "porannym przebudzeniu" ani "starcie dnia". Mów o: zamykaniu cyklu energetycznego, nocnej regeneracji, potrzebie wyciszenia, odpuszczeniu dnia.
- 04:00–10:00 = PORANEK: Przebudzenie, łagodny start, poranny przepływ.
- 10:00–17:00 = DZIEŃ: Pełna aktywność, przepływ twórczy, szczyt wibracji.
- 17:00–22:00 = WIECZÓR: Wyciszanie, zbieranie energii, refleksja.

ZASADA "HOROSKOPU" (CRITICAL):
- Twoje odpowiedzi muszą być jak luksusowy horoskop: każdy użytkownik powinien poczuć, że opis pasuje idealnie do niego.
- NIGDY nie pisz czegoś, co można łatwo zweryfikować jako fałsz (np. "Twoje ciało nosi ślady snu" — gdy ktoś nie spał).
- Używaj OTWARTYCH, UNIWERSALNYCH sformułowań, które brzmią personalnie: "Wyczuwam, że Twoje pole energetyczne przeszło dziś przez intensywne transformacje", "Widzę w Tobie głęboką gotowość do zmiany", "Twoja energia niesie w sobie zarówno siłę jak i potrzebę wyciszenia".
- Bazuj na tym co WIDZISZ na zdjęciu (napięcia, światło, wyraz), ale INTERPRETUJ to metaforycznie, nie dosłownie.
- Unikaj stwierdzeń o konkretnych czynnościach użytkownika (nie mów "wracasz z pracy", "właśnie się obudziłeś").

INSTRUKCJA ANALIZY:
A. Twarz: Szukaj napięć w żuchwie, oczach i brwiach. Opisz poetycko, bez terminów medycznych. Interpretuj energetycznie.
B. Prezencja fizyczna: Oceń ogólny stan energetyczny — czy energia jest rozproszona, skoncentrowana, wyciszona, czy w trybie ochronnym. Wpleć to naturalnie, bez nazywania konkretnych elementów wyglądu.
C. Otoczenie: Oceń tło i światło. Uwzględnij porę dnia — ciemne otoczenie nocą jest NATURALNE, nie oznacza problemu.
D. Pora dnia: Dopasuj ton i rekomendacje do aktualnej godziny. Nocą rekomenduj wyciszenie i regenerację, rano — łagodny start, w dzień — pełny przepływ.

FORMAT ZWROTNY:
Zwróć WYŁĄCZNIE czysty obiekt JSON. Żadnego markdowna, żadnego wstępu.
{
  "status_kategorii": "Wybierz jedno z: STRESS, SAD, NEUTRAL, HAPPY",
  "aura_color": "Podaj jeden kolor po polsku, np. Szmaragdowy, Złoty, Ametystowy, Szafirowy, Rubinowy, Perłowy, Srebrzysty, Koralowy",
  "wibracja_procent": <Liczba całkowita od 30 do 95>,
  "body_signals": "<Krótki, poetycki akapit (max 3 zdania) łączący analizę napięć twarzy i ogólnej prezencji energetycznej. Bez języka klinicznego. Bez dosłownego opisywania wyglądu. Interpretuj ENERGETYCZNIE. Uwzględnij porę dnia.>",
  "environment_resonance": "<Krótki akapit (max 2 zdania) oceniający tło i światło w ujęciu energetycznym. Uwzględnij porę dnia — nocne ciemne otoczenie to naturalny stan, nie problem. Poetycko, z wyczuciem wibracji przestrzeni.>"
}`;
async function generateAIReport(imageBase64: string): Promise<AIReport> {
  try {
    const compressed = await compressImage(imageBase64, 512, 0.6);
    const response = await fetch('https://www.requests.magicpatterns.com/openai/proxy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'gpt-4-turbo',
        messages: [{
          role: 'system',
          content: SCANNER_SYSTEM_PROMPT
        }, {
          role: 'user',
          content: [{
            type: 'image_url',
            image_url: {
              url: compressed,
              detail: 'low'
            }
          }, {
            type: 'text',
            text: `Przeanalizuj to zdjęcie i stwórz odczyt energetyczny. Aktualna godzina użytkownika: ${new Date().toLocaleTimeString('pl-PL', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })}. Dzień tygodnia: ${new Date().toLocaleDateString('pl-PL', {
              weekday: 'long'
            })}.`
          }]
        }],
        temperature: 0.9,
        max_tokens: 500
      })
    });
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    const data = await response.json();
    const content = data.choices?.[0]?.message?.content || '';
    // Parse JSON from response (handle potential markdown wrapping)
    let jsonStr = content;
    const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (jsonMatch) {
      jsonStr = jsonMatch[1];
    }
    const parsed = JSON.parse(jsonStr.trim());
    // Validate required fields
    if (!parsed.aura_color || !parsed.body_signals || !parsed.environment_resonance) {
      throw new Error('Incomplete report');
    }
    // Ensure status_kategorii is valid
    const validStatuses = ['STRESS', 'SAD', 'NEUTRAL', 'HAPPY'];
    if (!validStatuses.includes(parsed.status_kategorii)) {
      parsed.status_kategorii = 'NEUTRAL';
    }
    return {
      status_kategorii: parsed.status_kategorii,
      aura_color: parsed.aura_color,
      wibracja_procent: Math.min(95, Math.max(30, Number(parsed.wibracja_procent) || 65)),
      body_signals: parsed.body_signals,
      environment_resonance: parsed.environment_resonance
    };
  } catch (error) {
    console.error('AI report generation failed, using fallback:', error);
    // Fallback to random mock report
    return MOCK_REPORTS[Math.floor(Math.random() * MOCK_REPORTS.length)];
  }
}
async function recognizeEmotions(imageBase64: string): Promise<string> {
  const compressed = await compressImage(imageBase64, 512, 0.6);
  const response = await fetch('https://www.requests.magicpatterns.com/openai/proxy', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      model: 'gpt-4-turbo',
      messages: [{
        role: 'system',
        content: 'Jesteś ekspertem od rozpoznawania emocji na podstawie wyrazu twarzy. Analizujesz WYŁĄCZNIE emocje widoczne na zdjęciu — NIE identyfikujesz osoby. Opisz szczegółowo jakie emocje widzisz: mikroekspresje, napięcie mięśni twarzy, wyraz oczu, ust, brwi. Odpowiedz po polsku. Bądź szczegółowa i precyzyjna.'
      }, {
        role: 'user',
        content: [{
          type: 'image_url',
          image_url: {
            url: compressed,
            detail: 'low'
          }
        }, {
          type: 'text',
          text: 'Rozpoznaj emocje widoczne na tym zdjęciu. NIE identyfikuj osoby. Skup się wyłącznie na emocjach i wyrazie twarzy.'
        }]
      }],
      temperature: 0.7,
      max_tokens: 600
    })
  });
  if (!response.ok) throw new Error(`API error: ${response.status}`);
  const data = await response.json();
  return data.choices?.[0]?.message?.content || 'Brak odpowiedzi z AI';
}
const LOADING_PHASES = ['Odczytywanie aury...', 'Mapowanie pola energetycznego...', 'Analiza czakr i meridianów...', 'Synteza profilu wibracyjnego...', 'Łączenie z intuicją...'];
function MagicalLoadingText() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setPhase(p => (p + 1) % LOADING_PHASES.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);
  return <AnimatePresence mode="wait" data-id="element-515">
      <motion.p key={phase} className="text-white/90 text-xs font-medium tracking-wider" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -8
    }} transition={{
      duration: 0.5
    }} data-id="element-516">
        {LOADING_PHASES[phase]}
      </motion.p>
    </AnimatePresence>;
}
export function EmotionScanner({
  energyPoints,
  onDeductPoints,
  onOpenEnergyPopup,
  onClose,
  onGoHome,
  onStartRitual
}: EmotionScannerProps) {
  const [status, setStatus] = useState<ScannerStatus>('loading');
  const [progress, setProgress] = useState(0);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [report, setReport] = useState<AIReport | null>(null);
  const [displayedVibration, setDisplayedVibration] = useState(0);
  const [displayedAura, setDisplayedAura] = useState('');
  const [auraRevealed, setAuraRevealed] = useState(false);
  const {
    playClick
  } = useClickSound();
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement>(null);
  const faceMeshRef = useRef<any>(null);
  const cameraRef = useRef<any>(null);
  const statusRef = useRef<ScannerStatus>('loading');
  const faceDetectedRef = useRef(false);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  const onResults = useCallback((results: any) => {
    const canvas = overlayCanvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth || video.clientWidth;
      canvas.height = video.videoHeight || video.clientHeight;
    }
    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const hasFace = results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0;
    faceDetectedRef.current = hasFace;
    if (hasFace && (statusRef.current === 'ready' || statusRef.current === 'scanning')) {
      const isScanning = statusRef.current === 'scanning';
      const lineColor = isScanning ? '#9EB384' : 'rgba(188, 163, 127, 0.4)';
      const lineWidth = isScanning ? 1.5 : 0.5;
      for (const landmarks of results.multiFaceLandmarks) {
        if (window.drawConnectors && window.FACEMESH_TESSELATION) {
          window.drawConnectors(ctx, landmarks, window.FACEMESH_TESSELATION, {
            color: lineColor,
            lineWidth: lineWidth
          });
          window.drawConnectors(ctx, landmarks, window.FACEMESH_RIGHT_EYE, {
            color: '#BCA37F',
            lineWidth: 1.5
          });
          window.drawConnectors(ctx, landmarks, window.FACEMESH_LEFT_EYE, {
            color: '#BCA37F',
            lineWidth: 1.5
          });
          window.drawConnectors(ctx, landmarks, window.FACEMESH_LIPS, {
            color: '#BCA37F',
            lineWidth: 1.5
          });
          window.drawConnectors(ctx, landmarks, window.FACEMESH_FACE_OVAL, {
            color: '#BCA37F',
            lineWidth: 1
          });
        } else {
          // Fallback if drawing_utils not loaded yet
          ctx.fillStyle = lineColor;
          for (const landmark of landmarks) {
            const x = landmark.x * canvas.width;
            const y = landmark.y * canvas.height;
            ctx.beginPath();
            ctx.arc(x, y, isScanning ? 1.5 : 0.8, 0, 2 * Math.PI);
            ctx.fill();
          }
        }
      }
    }
    ctx.restore();
  }, []);
  const stopCamera = useCallback(() => {
    // Stop MediaPipe camera instance
    if (cameraRef.current) {
      try {
        cameraRef.current.stop();
      } catch (e) {
        /* ignore */
      }
      cameraRef.current = null;
    }
    // Stop all tracks on the video element
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (e) {
          /* ignore */
        }
      });
      videoRef.current.srcObject = null;
    }
    // Close FaceMesh if open
    if (faceMeshRef.current) {
      try {
        faceMeshRef.current.close();
      } catch (e) {
        /* ignore */
      }
      faceMeshRef.current = null;
    }
  }, []);
  const initMediaPipe = useCallback(async () => {
    if (!videoRef.current) return;
    // Ensure any previous camera is fully released first
    stopCamera();
    await new Promise(r => setTimeout(r, 500));
    try {
      await loadScript('https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js');
      await loadScript('https://cdn.jsdelivr.net/npm/@mediapipe/drawing_utils/drawing_utils.js');
      await loadScript('https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js');
      await new Promise(r => setTimeout(r, 100));
      if (!window.FaceMesh || !window.Camera) {
        throw new Error('MediaPipe globals not found');
      }
      const faceMesh = new window.FaceMesh({
        locateFile: (file: string) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`
      });
      faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });
      faceMesh.onResults(onResults);
      faceMeshRef.current = faceMesh;
      const camera = new window.Camera(videoRef.current, {
        onFrame: async () => {
          if (videoRef.current && faceMeshRef.current) {
            await faceMeshRef.current.send({
              image: videoRef.current
            });
          }
        },
        width: 480,
        height: 640,
        facingMode: 'user'
      });
      cameraRef.current = camera;
      await camera.start();
      setStatus('ready');
    } catch (err) {
      console.error('MediaPipe init error:', err);
      // Clean up any partial MediaPipe camera lock before fallback
      stopCamera();
      await new Promise(r => setTimeout(r, 500));
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: {
              ideal: 480
            },
            height: {
              ideal: 640
            }
          }
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setStatus('ready');
      } catch (camErr: any) {
        console.error('Camera fallback error:', camErr);
        setStatus('camera_error');
        setReportError(camErr?.name === 'NotReadableError' ? 'Kamera jest zajęta przez inną aplikację. Zamknij inne aplikacje używające kamery i spróbuj ponownie.' : camErr?.name === 'NotAllowedError' ? 'Brak dostępu do kamery. Zezwól na dostęp w ustawieniach przeglądarki.' : `Nie udało się uruchomić kamery: ${camErr?.message || 'Nieznany błąd'}`);
      }
    }
  }, [onResults, stopCamera]);
  useEffect(() => {
    initMediaPipe();
    return () => {
      stopCamera();
    };
  }, [initMediaPipe, stopCamera]);
  const capturePhoto = useCallback(() => {
    setFaceDetected(faceDetectedRef.current);
    if (videoRef.current && captureCanvasRef.current && overlayCanvasRef.current) {
      const video = videoRef.current;
      const captureCanvas = captureCanvasRef.current;
      const overlayCanvas = overlayCanvasRef.current;
      captureCanvas.width = video.videoWidth;
      captureCanvas.height = video.videoHeight;
      const ctx = captureCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, captureCanvas.width, captureCanvas.height);
        ctx.drawImage(overlayCanvas, 0, 0, captureCanvas.width, captureCanvas.height);
        const dataUrl = captureCanvas.toDataURL('image/jpeg', 0.9);
        setCapturedImage(dataUrl);
        setStatus('complete');
        stopCamera();
        try {
          navigator.vibrate?.([200, 100, 200]);
        } catch (e) {
          /* ignore */
        }
      }
    }
  }, [stopCamera]);
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (status === 'scanning') {
      setProgress(0);
      const duration = 6000;
      const interval = 50;
      const steps = duration / interval;
      let currentStep = 0;
      timer = setInterval(() => {
        currentStep++;
        setProgress(currentStep / steps * 100);
        if (currentStep >= steps) {
          clearInterval(timer);
          capturePhoto();
        }
      }, interval);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [status, capturePhoto]);
  // Auto-trigger report generation when face detected (skip "Generuj Odczyt" button)
  useEffect(() => {
    if (status === 'complete' && faceDetected && capturedImage) {
      const timer = setTimeout(() => {
        setStatus('generating_report');
        generateAIReport(capturedImage).then(aiReport => {
          setReport(aiReport);
          setStatus('showing_report');
        }).catch(err => {
          console.error('AI report generation error:', err);
          setReportError(`Błąd: ${err?.message || 'Nieznany błąd'}`);
          setStatus('complete');
          setFaceDetected(false);
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [status, faceDetected, capturedImage]);
  // Aura color "slot machine" animation
  const AURA_COLORS = ['Szmaragdowy', 'Złoty', 'Ametystowy', 'Rubinowy', 'Perłowy', 'Szafirowy', 'Koralowy', 'Turkusowy'];
  useEffect(() => {
    if (status === 'showing_report' && report) {
      setAuraRevealed(false);
      setDisplayedVibration(0);
      // Slot machine for aura color
      let colorIdx = 0;
      const slotInterval = setInterval(() => {
        setDisplayedAura(AURA_COLORS[colorIdx % AURA_COLORS.length]);
        colorIdx++;
      }, 100);
      // Stop slot machine after 2s and reveal real color
      const revealTimer = setTimeout(() => {
        clearInterval(slotInterval);
        setDisplayedAura(report.aura_color);
        setAuraRevealed(true);
      }, 2000);
      // Count up vibration from 0 to target
      const targetVib = report.wibracja_procent;
      const vibDuration = 2500;
      const vibInterval = 30;
      const vibSteps = vibDuration / vibInterval;
      let vibStep = 0;
      const vibTimer = setInterval(() => {
        vibStep++;
        const eased = 1 - Math.pow(1 - vibStep / vibSteps, 3); // ease-out cubic
        setDisplayedVibration(Math.round(eased * targetVib));
        if (vibStep >= vibSteps) {
          clearInterval(vibTimer);
          setDisplayedVibration(targetVib);
        }
      }, vibInterval);
      return () => {
        clearInterval(slotInterval);
        clearTimeout(revealTimer);
        clearInterval(vibTimer);
      };
    }
  }, [status, report]);
  const handleStartScan = () => {
    playClick();
    setStatus('scanning');
  };
  const handleRescan = () => {
    playClick();
    setCapturedImage(null);
    setProgress(0);
    setReportError(null);
    setReport(null);
    setFaceDetected(false);
    faceDetectedRef.current = false;
    stopCamera();
    setStatus('loading');
    // initMediaPipe has its own internal delay for camera release
    setTimeout(() => {
      initMediaPipe();
    }, 200);
  };
  const handleGenerateReport = async () => {
    playClick();
    if (!capturedImage) {
      setReportError('Brak zdjęcia do analizy. Spróbuj ponownie.');
      return;
    }
    setReportError(null);
    setStatus('generating_report');
    try {
      const aiReport = await generateAIReport(capturedImage);
      setReport(aiReport);
      setStatus('showing_report');
    } catch (err: any) {
      console.error('AI report generation error:', err);
      const detail = err?.message || 'Nieznany błąd';
      setReportError(`Błąd: ${detail}`);
      setStatus('complete');
    }
  };
  // Dynamic scanning text logic
  const getScanningPhase = () => {
    if (progress < 33) return {
      text: 'Mapowanie napięć twarzy...',
      icon: '🔴',
      color: 'text-red-400'
    };
    if (progress < 66) return {
      text: 'Analiza rezonansu otoczenia...',
      icon: '🟢',
      color: 'text-emerald-400'
    };
    return {
      text: 'Synteza profilu aury...',
      icon: '🔮',
      color: 'text-violet-400'
    };
  };
  const therapy = report ? THERAPY_MATRIX[report.status_kategorii] || THERAPY_MATRIX['NEUTRAL'] : THERAPY_MATRIX['NEUTRAL'];
  const TherapyIcon = therapy.icon;
  return <motion.div className="flex flex-col items-center w-full max-w-lg z-50 fixed inset-0 bg-slate-900 overflow-hidden" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} data-id="element-517">
      {/* Header */}
      <div className="w-full flex items-center justify-between p-6 absolute top-0 z-20" data-id="element-518">
        <button onClick={() => {
        playClick();
        onClose();
      }} className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white border border-white/20 hover:bg-white/20 transition-colors" aria-label="Zamknij" data-id="element-519">
          <X className="w-5 h-5" data-id="element-520" />
        </button>
        <span className="text-xs font-semibold tracking-widest uppercase text-white bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20" data-id="element-521">
          Skaner Aury - Twój odczyt
        </span>
        <div className="w-10 h-10 flex items-center justify-center" data-id="element-522">
          <div className="flex items-center space-x-1 bg-yellow-500/20 px-2 py-1 rounded-full border border-yellow-500/30 backdrop-blur-md" data-id="element-523">
            <Sparkles className="w-3 h-3 text-yellow-400" fill="currentColor" data-id="element-524" />
            <span className="text-xs font-bold text-yellow-400" data-id="element-525">
              {energyPoints}
            </span>
          </div>
        </div>
      </div>

      {/* Main Scanner Area */}
      <div className="relative w-full h-full max-w-lg mx-auto flex flex-col justify-center items-center overflow-hidden" data-id="element-526">
        {status === 'loading' && <div className="absolute z-10 flex flex-col items-center text-white" data-id="element-527">
            <Aperture className="w-10 h-10 animate-spin text-zen-accent mb-2" data-id="element-528" />
            <p className="text-xs tracking-widest uppercase opacity-70 mt-2" data-id="element-529">
              Ładowanie modelu AI...
            </p>
          </div>}

        {status === 'camera_error' && <motion.div className="absolute z-10 flex flex-col items-center text-white px-8 max-w-sm" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-530">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4 border border-red-500/30" data-id="element-531">
              <AlertCircle className="w-8 h-8 text-red-400" data-id="element-532" />
            </div>
            <p className="text-sm font-semibold text-white mb-2 text-center" data-id="element-533">
              Problem z kamerą
            </p>
            <p className="text-xs text-slate-400 text-center mb-6 leading-relaxed" data-id="element-534">
              {reportError || 'Nie udało się uruchomić kamery. Upewnij się, że żadna inna aplikacja nie używa kamery.'}
            </p>
            <button onClick={handleRescan} className="bg-zen-accent hover:bg-zen-accent/80 text-white rounded-xl px-6 py-3 text-sm font-bold transition-colors shadow-lg shadow-zen-accent/20 mb-3 w-full" data-id="element-535">
              Spróbuj ponownie
            </button>
            <button onClick={() => {
          playClick();
          onClose();
        }} className="bg-slate-800 border border-slate-700 text-slate-300 rounded-xl px-6 py-3 text-sm font-medium hover:bg-slate-700 transition-colors w-full" data-id="element-536">
              Wróć
            </button>
          </motion.div>}

        <canvas ref={captureCanvasRef} className="hidden" data-id="element-537" />

        {status === 'loading' || status === 'ready' || status === 'scanning' || status === 'camera_error' ? <>
            <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover -scale-x-100 opacity-80" data-id="element-538" />

            <canvas ref={overlayCanvasRef} className="absolute inset-0 w-full h-full object-cover -scale-x-100 pointer-events-none" data-id="element-539" />

            {/* UI Skanowania */}
            <div className={`absolute bottom-10 left-0 w-full px-6 flex flex-col items-center z-20 transition-opacity duration-300 ${status === 'ready' || status === 'scanning' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-540">
              <div className="flex items-center justify-center w-full mb-6 space-x-3" data-id="element-541">
                <div className="bg-white/90 backdrop-blur-md px-6 py-3 rounded-2xl shadow-lg text-center border border-white/50 min-w-[250px]" data-id="element-542">
                  <p className="text-sm font-semibold text-zen-text mb-1 flex items-center justify-center space-x-2" data-id="element-543">
                    {status === 'scanning' ? <>
                        <span className="text-base" data-id="element-544">
                          {getScanningPhase().icon}
                        </span>
                        <span data-id="element-545">{getScanningPhase().text}</span>
                      </> : 'Ustaw twarz w kadrze'}
                  </p>
                  <p className="text-[10px] text-slate-500" data-id="element-546">
                    {status === 'scanning' ? `${Math.round(progress)}%` : 'Gotowość do analizy Twojej energii'}
                  </p>
                </div>
              </div>

              {status === 'ready' && <button onClick={handleStartScan} className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm border-4 border-zen-accent flex items-center justify-center group transition-transform hover:scale-105 active:scale-95" data-id="element-547">
                  <div className="w-14 h-14 rounded-full bg-zen-accent flex items-center justify-center shadow-[0_0_20px_rgba(188,163,127,0.5)]" data-id="element-548">
                    <Aperture className="text-white w-8 h-8" data-id="element-549" />
                  </div>
                </button>}

              {status === 'scanning' && <div className="w-20 h-20 rounded-full border-4 border-zen-sage/30 flex items-center justify-center relative" data-id="element-550">
                  <svg className="absolute inset-0 w-full h-full -rotate-90" data-id="element-551">
                    <circle cx="36" cy="36" r="34" fill="none" stroke="#9EB384" strokeWidth="4" strokeDasharray="213" strokeDashoffset={213 - 213 * progress / 100} className="transition-all duration-75 ease-linear" data-id="element-552" />
                  </svg>
                  <div className="w-14 h-14 rounded-full bg-zen-sage flex items-center justify-center shadow-[0_0_20px_rgba(158,179,132,0.5)] animate-pulse" data-id="element-553">
                    <Aperture className="text-white w-8 h-8 animate-spin-slow" data-id="element-554" />
                  </div>
                </div>}
            </div>
          </> : <div className="absolute inset-0 w-full h-full flex flex-col overflow-y-auto pb-20" data-id="element-555">
            {/* Full-screen darkened background with face mesh */}
            <div className="absolute inset-0 z-0 bg-slate-900" data-id="element-556">
              {capturedImage && <>
                  <img src={capturedImage} alt="Captured Aura" className="absolute inset-0 w-full h-full object-cover -scale-x-100 object-top opacity-60" data-id="element-557" />
                  <div className="absolute inset-0 bg-slate-900/30" data-id="element-558" />
                </>}
            </div>

            {/* Magical generating overlay */}
            {status === 'generating_report' && <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pt-32" data-id="element-559">
                <div className="relative w-24 h-24 rounded-full flex items-center justify-center mb-8" data-id="element-560">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-zen-sage/40 to-emerald-400/20 blur-xl animate-pulse" data-id="element-561" />
                  <Aperture className="w-12 h-12 text-zen-sage animate-spin" data-id="element-562" />
                </div>
                <MagicalLoadingText data-id="element-563" />
              </div>}

            {/* Report UI */}
            {status === 'showing_report' && report && <motion.div className="w-full max-w-md mx-auto px-4 relative z-20 pt-16" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-564">
                <div className="bg-slate-800/50 backdrop-blur-md rounded-3xl p-6 border border-slate-700/50 shadow-2xl" data-id="element-565">
                  <div className="flex items-center space-x-3 mb-6 justify-center" data-id="element-566">
                    <motion.div className="flex items-center space-x-2 bg-zen-sage/10 px-3 py-1.5 rounded-full border border-zen-sage/20 overflow-hidden" animate={auraRevealed ? {
                scale: [1, 1.1, 1]
              } : {}} transition={{
                duration: 0.4
              }} data-id="element-567">
                      <motion.div className="w-3 h-3 rounded-full bg-emerald-400" animate={auraRevealed ? {
                  boxShadow: '0 0 12px rgba(52,211,153,0.8)'
                } : {
                  boxShadow: '0 0 4px rgba(52,211,153,0.3)'
                }} data-id="element-568" />
                      <span className={`text-xs font-medium text-zen-sage transition-all ${!auraRevealed ? 'blur-[1px]' : ''}`} data-id="element-569">
                        Aura: {displayedAura || report.aura_color}
                      </span>
                    </motion.div>
                    <div className="flex items-center space-x-1 bg-zen-accent/10 px-3 py-1.5 rounded-full border border-zen-accent/20" data-id="element-570">
                      <Sparkles className="w-3 h-3 text-zen-accent" data-id="element-571" />
                      <span className="text-xs font-bold text-zen-accent tabular-nums" data-id="element-572">
                        {displayedVibration}% Wibracji
                      </span>
                    </div>
                  </div>

                  <div className="space-y-5" data-id="element-573">
                    {/* Sygnały z Ciała */}
                    <div data-id="element-574">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-575">
                        <Eye className="w-4 h-4 text-slate-400" data-id="element-576" />
                        <h4 className="text-sm font-semibold text-slate-200" data-id="element-577">
                          Zwierciadło Duszy
                        </h4>
                      </div>
                      <div className="bg-slate-900/50 p-3.5 rounded-2xl border border-slate-700" data-id="element-578">
                        <p className="text-sm text-slate-400 leading-relaxed" data-id="element-579">
                          {report.body_signals}
                        </p>
                      </div>
                    </div>

                    {/* Rezonans Otoczenia */}
                    <div data-id="element-580">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-581">
                        <Home className="w-4 h-4 text-slate-400" data-id="element-582" />
                        <h4 className="text-sm font-semibold text-slate-200" data-id="element-583">
                          Rezonans Otoczenia
                        </h4>
                      </div>
                      <div className="bg-slate-900/50 p-3.5 rounded-2xl border border-slate-700" data-id="element-584">
                        <p className="text-sm text-slate-400 leading-relaxed" data-id="element-585">
                          {report.environment_resonance}
                        </p>
                      </div>
                    </div>

                    {/* Rekomendacja — driven by THERAPY_MATRIX */}
                    <div className="bg-zen-sage/10 p-5 rounded-2xl border border-zen-sage/20 mt-6 relative overflow-hidden" data-id="element-586">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-zen-sage/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-587"></div>

                      <p className="text-xs text-zen-sage font-semibold uppercase tracking-wider mb-4" data-id="element-588">
                        Rekomendowany Rytuał
                      </p>

                      <div className="flex items-center justify-between mb-4" data-id="element-589">
                        <div className="flex items-center space-x-3" data-id="element-590">
                          <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center shadow-sm border border-slate-700" data-id="element-591">
                            <TherapyIcon className="w-6 h-6 text-zen-sage" data-id="element-592" />
                          </div>
                          <div data-id="element-593">
                            <p className="text-base font-bold text-white" data-id="element-594">
                              {therapy.ritual_title}
                            </p>
                            <p className="text-xs text-slate-400" data-id="element-595">
                              {therapy.sound_name} • {therapy.breathing_name}
                            </p>
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-slate-300 italic mb-6" data-id="element-596">
                        "{therapy.ritual_description}"
                      </p>

                      <button onClick={() => {
                  playClick();
                  if (onStartRitual && report) {
                    onStartRitual(report.status_kategorii);
                  } else {
                    onClose();
                    onGoHome();
                  }
                }} className="w-full bg-zen-sage hover:bg-zen-sage/90 text-white rounded-xl py-3.5 text-sm font-bold transition-colors shadow-lg shadow-zen-sage/20" data-id="element-597">
                        Rozpocznij {therapy.ritual_title}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>}

            {/* Bottom Actions for Complete/Error state (only no-face or error) */}
            {status === 'complete' && !faceDetected && <motion.div className="w-full max-w-md mx-auto px-6 mt-8 flex flex-col space-y-4 relative z-20" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-598">
                <div className="bg-red-900/30 border border-red-500/30 rounded-2xl p-4 text-center" data-id="element-599">
                  <AlertCircle className="w-6 h-6 text-red-400 mx-auto mb-2" data-id="element-600" />
                  <p className="text-sm text-red-400 font-medium mb-1" data-id="element-601">
                    Nie wykryto twarzy
                  </p>
                  {reportError && <p className="text-xs text-red-400/70 mb-2" data-id="element-602">
                      {reportError}
                    </p>}
                  <button onClick={handleRescan} className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl py-3 text-sm font-medium hover:bg-slate-700 transition-colors mt-4" data-id="element-603">
                    Skanuj ponownie
                  </button>
                </div>
              </motion.div>}
          </div>}
      </div>
    </motion.div>;
}